import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Audiotrack as AudioIcon,
  Description as FileIcon,
  DataObject as JsonIcon,
  Image as ImageIcon,
  Movie as VideoIcon,
  PictureAsPdf as PdfIcon,
  TextFields as TextIcon,
} from '@mui/icons-material';
import { MODEL_REGISTRY, getAllModelOptions, getDisplayName, isDeprecated } from '../../config/modelConfig';
import { ModelSelector } from '../ModelSelector';
import {
  Expected,
  InputPreview,
  Material,
  MaterialKind,
  ModelGroup,
  ModelGroups,
  Pick,
  Picks,
  ROUTE_GROUP,
  Strictness,
  TestsApi,
  fmtCredits,
  fmtPctValue,
  fmtScore,
  fmtSeconds,
  scoreColor,
} from '../../services/testsApi';

export const KIND_ICONS: Record<string, React.ReactElement> = {
  text: <TextIcon fontSize="small" />,
  json: <JsonIcon fontSize="small" />,
  image: <ImageIcon fontSize="small" />,
  audio: <AudioIcon fontSize="small" />,
  video: <VideoIcon fontSize="small" />,
  pdf: <PdfIcon fontSize="small" />,
  file: <FileIcon fontSize="small" />,
};

export const KindChip: React.FC<{ kind: MaterialKind | 'missing' | string }> = ({ kind }) => (
  <Chip size="small" variant="outlined" icon={KIND_ICONS[kind]} label={kind} sx={{ textTransform: 'capitalize' }} />
);

// One object URL per material for the life of the page, so grids and dialogs share downloads.
const blobUrls = new Map<string, Promise<string>>();

export function useMaterialUrl(api: TestsApi, id: string | null | undefined, load = true): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!id || !load) return;
    let cancelled = false;
    let pending = blobUrls.get(id);
    if (!pending) {
      pending = api.materialBlob(id).then(b => URL.createObjectURL(b));
      pending.catch(() => blobUrls.delete(id));
      blobUrls.set(id, pending);
    }
    pending.then(u => !cancelled && setUrl(u)).catch(() => !cancelled && setUrl(null));
    return () => {
      cancelled = true;
    };
  }, [api, id, load]);
  return url;
}

/** Image thumbnail, or an audio/video player that loads when asked; text shows a snippet. */
export const MaterialPreview: React.FC<{ api: TestsApi; material: Material; height?: number }> = ({ api, material, height = 140 }) => {
  const [play, setPlay] = useState(false);
  const isMedia = material.kind === 'audio' || material.kind === 'video';
  const url = useMaterialUrl(api, material.id, material.has_file && (material.kind === 'image' || (isMedia && play)));
  const box = { height, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover', overflow: 'hidden' };
  if (material.kind === 'image') {
    return (
      <Box sx={box}>
        {url ? <img src={url} alt={material.name} style={{ maxWidth: '100%', maxHeight: height, objectFit: 'contain' }} /> : <CircularProgress size={20} />}
      </Box>
    );
  }
  if (isMedia) {
    return (
      <Box sx={box}>
        {!play ? (
          <Button size="small" startIcon={KIND_ICONS[material.kind]} onClick={() => setPlay(true)}>
            Play
          </Button>
        ) : !url ? (
          <CircularProgress size={20} />
        ) : material.kind === 'video' ? (
          <video src={url} controls style={{ maxWidth: '100%', maxHeight: height }} />
        ) : (
          <audio src={url} controls style={{ width: '95%' }} />
        )}
      </Box>
    );
  }
  return (
    <Box sx={{ ...box, alignItems: 'flex-start', justifyContent: 'flex-start', p: 1 }}>
      {material.text ? (
        <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', fontFamily: material.kind === 'json' ? 'monospace' : undefined }}>
          {material.text.slice(0, 400)}
        </Typography>
      ) : (
        <Stack alignItems="center" sx={{ width: '100%', mt: 3 }} spacing={0.5}>
          {KIND_ICONS[material.kind]}
          <Typography variant="caption" color="text.secondary">
            {material.file_name || material.kind}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

/** A case's inputs as small chips: kind plus a snippet or the material's name. */
export const InputChips: React.FC<{ preview: InputPreview[]; max?: number }> = ({ preview, max = 3 }) => (
  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
    {preview.slice(0, max).map((p, i) => (
      <Tooltip key={i} title={<span style={{ whiteSpace: 'pre-wrap' }}>{`${p.field}: ${p.text}`}</span>}>
        <Chip
          size="small"
          variant="outlined"
          icon={KIND_ICONS[p.type]}
          color={p.type === 'missing' ? 'error' : 'default'}
          label={p.text.length > 40 ? `${p.text.slice(0, 40)}…` : p.text || p.type}
          sx={{ maxWidth: 260 }}
        />
      </Tooltip>
    ))}
    {preview.length > max && <Chip size="small" label={`+${preview.length - max}`} />}
  </Stack>
);

export const Score: React.FC<{ value: number | null | undefined; bar?: number; suffix?: string }> = ({ value, bar, suffix }) => (
  <Box component="span" sx={{ color: scoreColor(value, bar), fontWeight: 600 }}>
    {fmtScore(value)}
    {suffix && value !== null && value !== undefined ? suffix : ''}
  </Box>
);

export const modelLabel = (m: string | null | undefined) =>
  m ? (m === 'default' ? 'Current default' : m === ROUTE_GROUP ? "Each route's model group" : getDisplayName(m) || m) : '–';

/** "3 models", or "route groups + 1 model" when the list has `route_group`. */
export const modelCountText = (models: string[]) => {
  const n = models.filter(m => m !== ROUTE_GROUP).length;
  const count = `${n} model${n === 1 ? '' : 's'}`;
  if (!models.includes(ROUTE_GROUP)) return count;
  return n ? `route groups + ${count}` : 'route groups';
};

export const PICK_LABELS: Record<'recommended' | 'quality' | 'budget', { label: string; color: 'primary' | 'secondary' | 'success'; help: string }> = {
  recommended: {
    label: 'Recommended',
    color: 'primary',
    help: 'Right the vast majority of the time (85+ accuracy, 90% of cases passing, reliable, within the platform deadline), then the best balance of speed and cost.',
  },
  quality: {
    label: 'High quality',
    color: 'secondary',
    help: 'The most accurate model, whatever it costs. Recommended itself when nothing is more accurate.',
  },
  budget: {
    label: 'Budget',
    color: 'success',
    help: 'The cheapest model that is still usable (70+ accuracy, reliable, within the deadline). Recommended itself when nothing is cheaper.',
  },
};

/** One pick in a table cell: model, accuracy, p95 and cost, with the reason on hover. */
export const PickCell: React.FC<{ pick: Pick | null | undefined; current?: string | null }> = ({ pick, current }) => {
  if (!pick) return <Typography variant="caption" color="text.secondary">–</Typography>;
  return (
    <Tooltip title={pick.why}>
      <Box sx={{ cursor: 'help' }}>
        <Typography variant="body2" sx={{ fontWeight: pick.model === current ? 400 : 600 }}>
          {modelLabel(pick.model)}
          {pick.model === current && (
            <Typography component="span" variant="caption" color="text.secondary">
              {' '}
              (current)
            </Typography>
          )}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          <Score value={pick.accuracy} /> acc · {fmtSeconds(pick.p95_s)} p95 · {fmtCredits(pick.credits_per_run)} cr
        </Typography>
      </Box>
    </Tooltip>
  );
};

/** The three picks as cards, with optional "Make default" per pick. */
export const PicksCards: React.FC<{
  picks: Picks | null | undefined;
  current?: string | null;
  onMakeDefault?: (model: string) => void;
  busyModel?: string | null;
}> = ({ picks, current, onMakeDefault, busyModel }) => {
  if (!picks || !(picks.recommended || picks.quality || picks.budget)) return null;
  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
        {(['recommended', 'quality', 'budget'] as const).map(k => {
          const p = picks[k];
          const meta = PICK_LABELS[k];
          return (
            <Box key={k} sx={{ flex: 1, border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <Tooltip title={meta.help}>
                <Chip size="small" color={meta.color} label={meta.label} sx={{ mb: 1, cursor: 'help' }} />
              </Tooltip>
              {p ? (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {modelLabel(p.model)}
                    {p.model === current && <Chip size="small" label="current" sx={{ ml: 1 }} />}
                  </Typography>
                  <Typography variant="body2">
                    <Score value={p.accuracy} /> accuracy · {fmtPctValue(p.pass_rate)} passing
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    p95 {fmtSeconds(p.p95_s)} · {fmtCredits(p.credits_per_run)} credits/run · {p.runs} runs
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                    {p.why}
                  </Typography>
                  {onMakeDefault && p.model !== current && (
                    <Button size="small" sx={{ mt: 1 }} disabled={!!busyModel} onClick={() => onMakeDefault(p.model)}>
                      {busyModel === p.model ? 'Saving…' : 'Make default'}
                    </Button>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No model fits.
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>
      {picks.note && (
        <Typography variant="caption" color="warning.main" component="div" sx={{ mt: 1 }}>
          {picks.note}
        </Typography>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Model checklist with quick picks
// ---------------------------------------------------------------------------

const liveTextModels = () =>
  Object.values(MODEL_REGISTRY).filter(m => m.category === 'text' && m.isAvailable !== false && !isDeprecated(m.apiName) && !m.variantOf);

/** Last two available text models declared per provider (the registry lists releases in order). */
export const newestModels = (): string[] => {
  const byProvider = new Map<string, string[]>();
  liveTextModels().forEach(m => byProvider.set(m.provider, [...(byProvider.get(m.provider) || []), m.apiName]));
  return Array.from(byProvider.values()).flatMap(list => list.slice(-2));
};

export interface QuickPickContext {
  defaultModel?: string | null;
  allowedModels?: string[];
  needsVision?: boolean;
}

const GROUPS_TTL_MS = 5 * 60 * 1000;
let groupsCache: { at: number; value: Promise<ModelGroups> } | null = null;

/** Drop the cached model groups (after saving them). */
export const forgetModelGroups = () => {
  groupsCache = null;
};

/** The model groups, fetched once and shared by every checklist for a few minutes. */
export const useModelGroups = (api?: TestsApi | null): ModelGroups | null => {
  const [groups, setGroups] = useState<ModelGroups | null>(null);
  useEffect(() => {
    if (!api) return;
    let live = true;
    if (!groupsCache || Date.now() - groupsCache.at > GROUPS_TTL_MS) {
      const value = api.modelGroups();
      groupsCache = { at: Date.now(), value };
      value.catch(() => {
        if (groupsCache?.value === value) groupsCache = null;
      });
    }
    groupsCache.value.then(g => live && setGroups(g)).catch(() => undefined);
    return () => {
      live = false;
    };
  }, [api]);
  return groups;
};

export const quickPicks = (ctx: QuickPickContext, groups: ModelGroup[] = []): { id: string; label: string; models: string[] }[] => {
  const live = liveTextModels();
  const def = ctx.defaultModel ? MODEL_REGISTRY[ctx.defaultModel] : undefined;
  const fits = (apiName: string) => !ctx.needsVision || !!MODEL_REGISTRY[apiName]?.supportsVision;
  const picks = [
    { id: 'default', label: 'Current default', models: ['default'] },
    ...groups.map(g => ({ id: `group:${g.id}`, label: g.name, models: g.models.filter(m => MODEL_REGISTRY[m] && !isDeprecated(m)) })),
    { id: 'allowed', label: 'Allowed models', models: (ctx.allowedModels || []).filter(m => MODEL_REGISTRY[m] && !isDeprecated(m)) },
    { id: 'tier', label: 'Same tier', models: def ? live.filter(m => m.costRating === def.costRating).map(m => m.apiName) : [] },
    { id: 'newest', label: 'Newest releases', models: newestModels() },
    { id: 'vision', label: 'All vision-capable', models: live.filter(m => m.supportsVision).map(m => m.apiName) },
  ];
  return picks.map(p => ({ ...p, models: p.models.filter(m => m === 'default' || fits(m)) })).filter(p => p.models.length);
};

export const MAX_MODELS = 12;

export const ModelChecklist: React.FC<{
  value: string[];
  onChange: (v: string[]) => void;
  context: QuickPickContext;
  allowDefault?: boolean;
  /** Loads the model groups for quick picks. */
  api?: TestsApi | null;
  /** Platform routes: offer "Each route's model group" (the `route_group` token). */
  allowRouteGroup?: boolean;
}> = ({ value, onChange, context, allowDefault = true, api, allowRouteGroup = false }) => {
  const groups = useModelGroups(api);
  const options = useMemo(() => {
    const live = new Set(liveTextModels().filter(m => !context.needsVision || m.supportsVision).map(m => m.apiName));
    return getAllModelOptions().filter(o => live.has(o.value));
  }, [context.needsVision]);
  const withDefault = allowDefault && value.includes('default');
  const withGroup = allowRouteGroup && value.includes(ROUTE_GROUP);
  const models = value.filter(m => m !== 'default' && m !== ROUTE_GROUP);
  const set = (next: string[], keepDefault = withDefault, keepGroup = withGroup) =>
    onChange(Array.from(new Set([...(keepDefault ? ['default'] : []), ...(keepGroup ? [ROUTE_GROUP] : []), ...next])).slice(0, MAX_MODELS));
  const add = (picked: string[]) => set([...models, ...picked.filter(m => m !== 'default')], withDefault || picked.includes('default'));
  return (
    <Box>
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5, mb: 1.5 }}>
        {quickPicks(context, groups?.groups)
          .filter(p => allowDefault || p.id !== 'default')
          .map(p => (
            <Chip key={p.id} size="small" label={`+ ${p.label}`} onClick={() => add(p.models)} variant="outlined" />
          ))}
        {value.length > 0 && <Chip size="small" label="Clear" onClick={() => onChange([])} />}
      </Stack>
      {allowDefault && (
        <FormControlLabel
          control={<Checkbox size="small" checked={withDefault} onChange={() => set(models, !withDefault)} />}
          label={<Typography variant="body2">Current default{context.defaultModel ? ` (${modelLabel(context.defaultModel)})` : ''}</Typography>}
          sx={{ display: 'flex', mb: 0.5 }}
        />
      )}
      {allowRouteGroup && (
        <FormControlLabel
          control={<Checkbox size="small" checked={withGroup} onChange={() => set(models, withDefault, !withGroup)} />}
          label={<Typography variant="body2">Each route's model group</Typography>}
          sx={{ display: 'flex', mb: 0.5 }}
        />
      )}
      <ModelSelector
        multiple
        ordered={false}
        size="small"
        label={withGroup ? 'More models for every route' : 'Models'}
        value={models}
        options={options}
        onChange={next => set(next as string[])}
      />
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
        {!value.length
          ? 'Pick at least one model.'
          : withGroup
            ? `Each route tests on its group${withDefault ? ', its current default' : ''}${models.length ? ` and ${models.length} more` : ''}, up to ${MAX_MODELS} models.`
            : `${value.length} of up to ${MAX_MODELS} models.`}
      </Typography>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Expected output editor
// ---------------------------------------------------------------------------

const lines = (v?: string[]) => (v || []).join('\n');
const split = (v: string) => v.split(/\n|,/).map(s => s.trim()).filter(Boolean);

export const ExpectedEditor: React.FC<{ value: Expected; onChange: (v: Expected) => void; compact?: boolean }> = ({ value, onChange, compact }) => {
  const [advanced, setAdvanced] = useState(
    !!(value.must_include?.length || value.must_not_include?.length || value.field_checks?.length || (value.strictness && value.strictness !== 'normal')),
  );
  const [checksText, setChecksText] = useState(value.field_checks?.length ? JSON.stringify(value.field_checks, null, 2) : '');
  const [checksError, setChecksError] = useState<string | null>(null);
  return (
    <Box>
      <TextField
        label="What should the output contain?"
        placeholder="e.g. grilled chicken and white rice, around 500 kcal"
        value={value.description || ''}
        onChange={e => onChange({ ...value, description: e.target.value })}
        fullWidth
        multiline
        minRows={compact ? 1 : 2}
        size="small"
        helperText="Plain words are fine. The judge checks meaning, not wording."
      />
      <Link component="button" type="button" variant="caption" onClick={() => setAdvanced(a => !a)} sx={{ mt: 0.5 }}>
        {advanced ? 'Hide advanced' : 'Advanced: must include, must not include, field checks, strictness'}
      </Link>
      <Collapse in={advanced}>
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              label="Must include (one per line)"
              value={lines(value.must_include)}
              onChange={e => onChange({ ...value, must_include: split(e.target.value) })}
              multiline
              minRows={2}
              size="small"
              fullWidth
            />
            <TextField
              label="Must not include (one per line)"
              value={lines(value.must_not_include)}
              onChange={e => onChange({ ...value, must_not_include: split(e.target.value) })}
              multiline
              minRows={2}
              size="small"
              fullWidth
            />
          </Stack>
          <Stack direction="row" spacing={1.5}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Strictness</InputLabel>
              <Select
                label="Strictness"
                value={value.strictness || 'normal'}
                onChange={e => onChange({ ...value, strictness: e.target.value as Strictness })}
              >
                <MenuItem value="lenient">Lenient</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="strict">Strict</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Pass at score"
              type="number"
              size="small"
              value={value.pass_threshold ?? 70}
              onChange={e => onChange({ ...value, pass_threshold: Math.max(1, Math.min(100, Number(e.target.value) || 70)) })}
              sx={{ width: 140 }}
            />
          </Stack>
          <TextField
            label="Field checks (JSON)"
            placeholder={'[{"path": "items.name", "op": "contains", "value": "rice"}, {"path": "nutrients[name=protein].amount", "op": "approx", "value": {"target": 12, "pct": 5}}]'}
            value={checksText}
            onChange={e => {
              setChecksText(e.target.value);
              if (!e.target.value.trim()) {
                setChecksError(null);
                onChange({ ...value, field_checks: [] });
                return;
              }
              try {
                const parsed = JSON.parse(e.target.value);
                if (!Array.isArray(parsed)) throw new Error('Use a list of checks');
                setChecksError(null);
                onChange({ ...value, field_checks: parsed });
              } catch (err) {
                setChecksError(err instanceof Error ? err.message : 'Invalid JSON');
              }
            }}
            error={!!checksError}
            helperText={
              checksError ||
              'Paths: a.b, items[0].name, items.name (every item), items[name=protein] (items whose name contains it). Ops: equals, contains, between ([min, max]), approx ({"target": 250, "pct": 5}), count_between ([min, max]).'
            }
            multiline
            minRows={2}
            size="small"
            fullWidth
            sx={{ fontFamily: 'monospace' }}
          />
        </Stack>
      </Collapse>
    </Box>
  );
};

/** Plain-text summary of an expectation, for tables. */
export const expectedSummary = (e: Expected | null | undefined): string => {
  if (!e) return '';
  const parts = [e.description?.trim()];
  if (e.must_include?.length) parts.push(`must include ${e.must_include.join(', ')}`);
  if (e.must_not_include?.length) parts.push(`must not include ${e.must_not_include.join(', ')}`);
  if (e.field_checks?.length) parts.push(`${e.field_checks.length} field check${e.field_checks.length === 1 ? '' : 's'}`);
  return parts.filter(Boolean).join(' · ');
};

export const timeAgo = (iso: string | null | undefined) => {
  if (!iso) return '–';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};
